'use client'

import { ApolloClient } from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'

import type { Client as GraphQLWsClient } from 'graphql-ws'

import { Store } from './Store'

export enum SharedService {
  ApolloClient = 'apolloClient',
  FloodgateLink = 'floodgateLink',
  Store = 'store',

  MainSubscription = 'mainSubscription',
  ChatSubscription = 'chatSubscription',
}

type SubscriptionResources = {
  client: GraphQLWsClient
  link: GraphQLWsLink
  url: string
}

const SERVICES: Record<string, any> = {}

function getService<T = any>(service: SharedService): T {
  const instance = SERVICES[service]
  if (!instance) throw new Error(`${service} not found in Shared`)
  return instance as T
}

// Apollo Client
function setApolloClient(client: ApolloClient<any>) {
  SERVICES[SharedService.ApolloClient] = client
}
function getApolloClient() {
  return getService<ApolloClient<any>>(SharedService.ApolloClient)
}

// Store
function setStore(store: Store) {
  SERVICES[SharedService.Store] = store
}
function getStore() {
  return getService<Store>(SharedService.Store)
}

// Subscription: Main & Chat
function setSubscriptionResources(
  kind: 'main' | 'chat',
  resources: SubscriptionResources,
) {
  const key =
    kind === 'main'
      ? SharedService.MainSubscription
      : SharedService.ChatSubscription
  SERVICES[key] = resources
}

function getSubscriptionResources(
  kind: 'main' | 'chat',
): SubscriptionResources | undefined {
  const key =
    kind === 'main'
      ? SharedService.MainSubscription
      : SharedService.ChatSubscription
  return SERVICES[key]
}

// Aliases
function getMainSubscriptionClient() {
  return getSubscriptionResources('main')?.client
}
function getChatSubscriptionClient() {
  return getSubscriptionResources('chat')?.client
}
function getMainSubscriptionUrl() {
  return getSubscriptionResources('main')?.url
}
function getChatSubscriptionUrl() {
  return getSubscriptionResources('chat')?.url
}
function getMainSubscriptionLink() {
  return getSubscriptionResources('main')?.link
}
function getChatSubscriptionLink() {
  return getSubscriptionResources('chat')?.link
}

const Shared = {
  // Core services
  setApolloClient,
  getApolloClient,
  setStore,
  getStore,

  // Subscriptions
  setSubscriptionResources,
  getSubscriptionResources,

  getMainSubscriptionClient,
  getMainSubscriptionLink,
  getMainSubscriptionUrl,

  getChatSubscriptionClient,
  getChatSubscriptionLink,
  getChatSubscriptionUrl,
}

export default Shared
